import { useMemo } from "react";

/*
 * The one place that knows what a Work Desk URL looks like. The parent and the
 * embedded client-card app address the same views with the same URLs, differing
 * only by a /workdesk segment after the hub id. Add a question here rather than
 * re-describing that shape at a call site — the boot src doing exactly that is
 * what let the iframe open the queue while the parent URL pointed at a client.
 */

function readHubPath(pathname) {
  const [, root, hubId, ...segments] = pathname.split("/");

  if (root !== "hub" || !hubId) {
    return null;
  }

  const inWorkdesk = segments[0] === "workdesk";
  const view = inWorkdesk ? segments.slice(1) : segments;

  return {
    hubRoot: `/hub/${hubId}`,
    inWorkdesk: inWorkdesk,
    clientView: view[0] === "clients" && view[1] ? view : null,
  };
}

function isWorkdeskPath(pathname) {
  return Boolean(readHubPath(pathname)?.inWorkdesk);
}

function isClientCardPath(pathname) {
  const path = readHubPath(pathname);

  return Boolean(path && !path.inWorkdesk && path.clientView);
}

// the modal client card embeds the same app without a /workdesk segment
function embedsClientCardIframe(pathname) {
  return isWorkdeskPath(pathname) || isClientCardPath(pathname);
}

// Views the embedded app has no route for (hub feed, settings, tools) resolve to
// the queue, so a preloaded iframe always boots somewhere it can render.
function toIframePath(pathname) {
  const path = readHubPath(pathname);

  if (!path) {
    return pathname;
  }

  return path.clientView
    ? `${path.hubRoot}/${path.clientView.join("/")}`
    : path.hubRoot;
}

// `inWorkdesk` because the modal client card has no /workdesk route of its own
function toParentPath(pathname, inWorkdesk) {
  const path = readHubPath(pathname);

  if (!inWorkdesk || !path) {
    return pathname;
  }
  const view = path.clientView ? `/${path.clientView.join("/")}` : "";

  return `${path.hubRoot}/workdesk${view}`;
}

export function useWorkDeskUrl() {
  // pure mappings, so identities stay stable for effect dependencies
  return useMemo(() => {
    return {
      isWorkdeskPath: isWorkdeskPath,
      isClientCardPath: isClientCardPath,
      embedsClientCardIframe: embedsClientCardIframe,
      toIframePath: toIframePath,
      toParentPath: toParentPath,
    };
  }, []);
}

export default useWorkDeskUrl;
